/* eslint-disable react/jsx-no-bind */
import { Control, Controller, FieldErrors, UseFormSetValue } from 'react-hook-form';
import { TimePicker } from '@hqo/date-time';
import { useIntl } from 'react-intl';
import { useLocale } from 'shared/hooks/use-locale/use-locale.hook';
import { CreateVisitFormV2Values } from '../../../types/form-data.types';
import { DatePickerField } from 'components/v2/date-picker-button';
import { TimeInputField } from './time-input-field';
import { DurationInputField } from './duration-input-field';
import { TimeModeSelector } from './time-mode-selector';
import { useRef } from 'react';
import { useValidationMessage } from '../../../hooks/use-validation-message.hook';

interface DateTimeInputGroupProps {
  control: Control<CreateVisitFormV2Values>;
  errors: FieldErrors<CreateVisitFormV2Values>;
  timeMode: 'duration' | 'endTime';
  startTime?: string;
  endTimeDisplay?: string;
  onTimeModeChange: (mode: 'duration' | 'endTime', onChange: (value: boolean) => void) => void;
  onDurationChange?: (value: string, onChange: (num: number) => void) => void;
  setValue?: UseFormSetValue<CreateVisitFormV2Values>;
  durationHidden?: boolean;
  maxStartDate?: Date;
}

export const DateTimeInputGroup = ({
  control,
  errors,
  timeMode,
  startTime,
  endTimeDisplay,
  onTimeModeChange,
  onDurationChange,
  setValue,
  durationHidden = false,
  maxStartDate,
}: DateTimeInputGroupProps): JSX.Element => {
  const { formatMessage } = useIntl();
  const locale = useLocale();
  const formatValidationMessage = useValidationMessage();
  const previousValue = useRef<string | null>(null);

  return (
    <div className="grid grid-cols-1 gap-x-6 gap-y-0 lg:grid-cols-2">
      {/* Start Date Field */}
      <DatePickerField
        name="visitStartDate"
        control={control}
        errors={errors}
        label={formatMessage({
          id: 'createVisitFormV2.whenStep.startDate',
          defaultMessage: 'Start date',
        })}
        id="startDate"
        minDate={new Date()}
        maxDate={maxStartDate}
        required
        setValue={setValue}
      />

      {/* Start Time Field */}
      <TimeInputField
        name="startTime"
        control={control}
        errors={errors}
        label={formatMessage({
          id: 'createVisitFormV2.whenStep.startTime',
          defaultMessage: 'Start time',
        })}
        id="startTime"
        placeholder={formatMessage({
          id: 'createVisitFormV2.whenStep.selectMeetingTime',
          defaultMessage: 'Select meeting time',
        })}
        required
      />

      <div className="flex flex-col">
        <div className="mb-1.5 h-7 flex items-center">
          {durationHidden ? (
            <span className="text-sm font-medium text-gray-900">
              {formatMessage({
                id: 'createVisitFormV2.whenStep.endTime',
                defaultMessage: 'End time',
              })}
              <span className="text-destructive ml-1">*</span>
            </span>
          ) : (
            <Controller
              name="useDuration"
              control={control}
              // eslint-disable-next-line react/jsx-no-bind
              render={({ field }) => (
                <TimeModeSelector
                  value={timeMode}
                  onDurationChange={() => onTimeModeChange('duration', field.onChange)}
                  onEndTimeChange={() => onTimeModeChange('endTime', field.onChange)}
                  required
                />
              )}
            />
          )}
        </div>
        {!durationHidden && timeMode === 'duration' ? (
          <DurationInputField
            control={control}
            errors={errors}
            startTime={startTime}
            endTimeDisplay={endTimeDisplay}
            onDurationChange={onDurationChange}
          />
        ) : (
          <Controller
            name="endTime"
            control={control}
            // eslint-disable-next-line react/jsx-no-bind
            render={({ field }) => {
              const error = errors.endTime;
              return (
                <>
                  <div className="w-full">
                    <TimePicker
                      id="endTime"
                      locale={locale}
                      timeFormat="auto"
                      // eslint-disable-next-line react/jsx-no-bind
                      onValueChange={(value) => {
                        // Convert null to empty string to avoid validation errors
                        const newValue = value === null ? '' : value || '';
                        if (previousValue.current !== newValue) {
                          previousValue.current = newValue;
                          field.onChange(newValue);
                        }
                      }}
                      placeholder={formatMessage({
                        id: 'createVisitFormV2.whenStep.selectEndTime',
                        defaultMessage: 'Select end time',
                      })}
                      timeInterval={30}
                      value={field.value || ''}
                      minTime={startTime || undefined}
                    />
                  </div>
                  <div className="mt-1 h-5">
                    {error && (
                      <p className="text-sm text-destructive">{formatValidationMessage(error.message as string)}</p>
                    )}
                  </div>
                </>
              );
            }}
          />
        )}
      </div>
    </div>
  );
};
